<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import Uppy from "@uppy/core"
import ImageEditor from "@uppy/image-editor"
import DashboardModal from "@uppy/vue/dashboard-modal"
import { onBeforeUnmount, ref } from "vue"
import { z } from "zod"

import "@uppy/core/css/style.min.css"
import "@uppy/dashboard/css/style.min.css"
import "@uppy/image-editor/css/style.min.css"
import FlashAlerts from "@/components/FlashAlerts.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import ProfileHeader from "@/components/ProfileHeader.vue"
import { useAuthUser } from "@/composables/useAuthUser"
import { useNavbar } from "@/composables/useNavbar"
import { csrfToken } from "@/lib/fetch"
import { DEFAULT_IMAGE_EDITOR_ACTIONS } from "@/lib/uppy"
import { profilePath, profileAvatarPath } from "@/routes"

interface Props {
  user: {
    first_name: string
    last_name: string
    email_address: string
  }
}

const props = defineProps<Props>()

const { title } = useNavbar({ title: "Edit Profile" })
const { avatarSrc, authUser } = useAuthUser()

const loading = ref(false)

const schema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email_address: z.string().email("Please enter a valid email address"),
})

const formState = ref({
  first_name: props.user.first_name,
  last_name: props.user.last_name,
  email_address: props.user.email_address,
})

function submitProfile() {
  loading.value = true
  router.patch(profilePath(), formState.value, {
    onFinish: () => (loading.value = false),
  })
}

const uppyModalOpen = ref(false)

const uppy = new Uppy({
  restrictions: {
    maxNumberOfFiles: 1,
    maxFileSize: 5 * 1024 * 1024,
    allowedFileTypes: ["image/jpeg", "image/png", "image/webp"],
  },
})

uppy.use(ImageEditor, {
  actions: DEFAULT_IMAGE_EDITOR_ACTIONS,
  cropperOptions: {
    aspectRatio: 1,
    viewMode: 1,
  },
})

uppy.on("file-editor:complete", (updatedFile) => {
  uppyModalOpen.value = false
  uploadAvatar(updatedFile.data as Blob)
})

uppy.on("cancel-all", () => {
  uppyModalOpen.value = false
})

onBeforeUnmount(() => {
  uppy.destroy()
})

async function uploadAvatar(blob: Blob) {
  const formData = new FormData()
  formData.append("user[avatar]", blob, "avatar.jpg")

  try {
    const res = await fetch(profileAvatarPath(), {
      method: "PATCH",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    })
    if (res.ok) {
      router.reload()
    }
  } finally {
    uppy.clear()
  }
}

function removeAvatar() {
  router.delete(profileAvatarPath(), {
    preserveScroll: true,
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <FlashAlerts />

    <ProfileHeader
      :avatar-src="avatarSrc"
      :initials="authUser?.initials"
      :name="authUser?.first_name ?? ''"
      :email="authUser?.email_address ?? ''"
    >
      <div class="flex gap-4 text-sm">
        <button type="button" class="text-primary cursor-pointer" @click="uppyModalOpen = true">
          Change photo
        </button>
        <button
          v-if="avatarSrc"
          type="button"
          class="text-muted cursor-pointer"
          @click="removeAvatar"
        >
          Remove
        </button>
      </div>
    </ProfileHeader>

    <!-- Profile form -->
    <UForm :schema="schema" :state="formState" class="flex flex-col gap-4" @submit="submitProfile">
      <UFormField label="First name" name="first_name" required>
        <UInput v-model="formState.first_name" autocomplete="given-name" class="w-full" />
      </UFormField>

      <UFormField label="Last name" name="last_name" required>
        <UInput v-model="formState.last_name" autocomplete="family-name" class="w-full" />
      </UFormField>

      <UFormField label="Email" name="email_address" required>
        <UInput
          v-model="formState.email_address"
          type="email"
          autocomplete="email"
          class="w-full"
        />
      </UFormField>

      <UButton type="submit" label="Save Changes" :loading="loading" block />
      <UButton :to="profilePath()" variant="link" label="Cancel" class="text-muted" block />
    </UForm>

    <!-- Uppy DashboardModal -->
    <DashboardModal
      :uppy="uppy"
      :open="uppyModalOpen"
      :props="{
        autoOpen: 'imageEditor',
        proudlyDisplayPoweredByUppy: false,
        note: 'JPG, PNG or WebP, up to 5 MB',
        hideProgressDetails: true,
        hideUploadButton: true,
      }"
    />
  </NarrowPage>
</template>
